import React, { Dispatch, SetStateAction, useState } from "react"
import { CartContainer, CartItems } from "../../Order/ManageOrder/RefundOrder/Cart/Cart.styled"
import * as graphql from "lib/types/generated/graphql-types"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderHistory,
  OrderOperationSearchHit,
  OrderReleaseSearchHit
} from "lib/types/generated/graphql-types"
import Reason from "../../Order/Updates/Reason"
import { ArrowIcon, OrderLabel } from "../Shared.styled"
import { Link } from "react-router-dom"
import OperationOrderLine from "./OperationOrderLine"
import OperationShippingFee from "./OperationShippingFee"
import OperationGiftCardProduct from "./OperationGiftCardProduct"
import StartOperation from "./StartOperation"
import { useMutation } from "@apollo/client"
import { handleErrorMessages } from "helpers/errors"
import START_RELEASE from "../../../graphql/mutations/order/releases/StartRelease"
import PendingRelease from "../../Order/ManageOrder/ReleaseOrder/PendingRelease"
import FailedRelease from "../../Order/ManageOrder/ReleaseOrder/FailedRelease"

type Props = {
  operation: OrderReleaseSearchHit
  order: Order
  orderHistory: OrderHistory
  getCustomCode: (code: Maybe<string> | undefined) => CustomReasonCode
  setShow: Dispatch<SetStateAction<boolean>>
  refetchOrder: () => void
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
}

const ReleaseAction = ({
  operation,
  order,
  refetchOrder,
  orderHistory,
  getCustomCode,
  setShow,
  setCreatedOperations,
  setFailedOperations
}: Props) => {
  const [giftCardAction, setGiftCardAction] = useState<graphql.InputActionGiftCard>({
    actionType: graphql.InputActionType.Auto
  })
  const [bonusAction, setBonusAction] = useState<graphql.InputActionBonus>({
    actionType: graphql.InputActionType.Auto
  })
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentRefund>({
    actionType: graphql.InputActionType.Auto
  })

  const release = order.Releases.find((release) => release.id === operation.id)

  const [startRelease, { loading }] = useMutation(START_RELEASE, {
    onCompleted: () => {
      setShow(false)
      setCreatedOperations((prev) => prev.filter((hit) => hit.id !== operation.id))
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const startOrderRelease = () => {
    startRelease({
      variables: {
        releaseId: release?.id,
        input: {
          ...(release?.bonus && { bonus: bonusAction }),
          ...(release?.giftCards && release?.giftCards?.length > 0 && { giftCard: giftCardAction }),
          payment: paymentAction
        }
      }
    })
  }

  const getOrderLine = (releaseLineId: string) => {
    return order?.orderLines?.find((orderLine) => orderLine.id === releaseLineId)
  }

  const getShippingFee = (releaseFeeId: string) => {
    return order?.shippingFees?.find((fee) => fee.id === releaseFeeId)
  }

  const isReleaseFailed = release?.failed && !release.completed
  const isReleasePending = release?.started && !release.completed && !release?.failed

  if (isReleasePending)
    return (
      <PendingRelease pendingRelease={release} orderRefetch={refetchOrder} orderLoading={loading} />
    )

  if (isReleaseFailed)
    return (
      <>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>
        <FailedRelease
          failedRelease={release}
          orderStatus={orderHistory.history}
          setFailedOperations={setFailedOperations}
          totals={operation.totals}
          currencyCode={order.currencyCode}
        />
      </>
    )

  return (
    <>
      <CartContainer key={release?.id}>
        <OrderLabel>
          Order: {order.reference}{" "}
          <Link to={`/orders/manage-order/${order.id}`} target="_blank">
            <ArrowIcon />
          </Link>
        </OrderLabel>

        <CartItems>
          {release?.orderLines.map((ol) => (
            <OperationOrderLine
              key={ol.orderLineId}
              orderLine={getOrderLine(ol.orderLineId)}
              orderQuantity={ol.quantity}
            />
          ))}
          {release?.shippingFees?.map((shippingFee) => (
            <OperationShippingFee
              shippingFee={getShippingFee(shippingFee.shippingFeeId)}
              key={shippingFee.shippingFeeId}
            />
          ))}
          {release?.giftCardProducts?.map((giftCardProduct) => (
            <OperationGiftCardProduct
              giftCardProduct={giftCardProduct}
              key={giftCardProduct.giftCardProductId}
            />
          ))}
          <Reason reason={release?.reason} getCustomCode={getCustomCode} />
        </CartItems>
        <StartOperation
          operation={release}
          actionState={{
            giftCardAction,
            setGiftCardAction,
            bonusAction,
            setBonusAction,
            paymentAction,
            setPaymentAction
          }}
          startOperation={startOrderRelease}
          loading={loading}
        />
      </CartContainer>
    </>
  )
}

export default ReleaseAction
